// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CartPageContainerMessagesContext } from "@bg/App/Components/Cart/CartPageContainerMessagesContextWrapper";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import { constants } from "@bg/XDL/constants";
import { hasLocalStorage } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
}

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;

    const {
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);

    const isQuoteCart = cart?.type === "Quote";
    const hasNonMatchQty = cart?.cartLines?.some(d => {
        const exactQty = d.properties.exactQty ? +d.properties.exactQty : undefined;
        const minimumQty = d.properties.minimumQty ? +d.properties.minimumQty : undefined;

        return (
            (exactQty !== undefined && d.qtyOrdered !== null && d.qtyOrdered !== exactQty) ||
            (minimumQty !== undefined && d.qtyOrdered !== null && d.qtyOrdered < minimumQty)
        );
    });
    const qtyCheckFailed = isQuoteCart && hasNonMatchQty;
    const noValidCredit = cart?.properties.isValidCredit ? cart?.properties.isValidCredit === "false" : false;

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest ||
            qtyCheckFailed ||
            noValidCredit,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        isAuth: getSession(state).isAuthenticated,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        // XNG-Changes
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        enablePostalCodeLocationFinder: enablePostalCodeLocationFinder(state),
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    linksContainer?: InjectableCss;
    cancelPunchOutButton?: LinkPresentationProps;
    submitQuoteButton?: LinkPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: LinkPresentationProps;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            margin: 20px 0;
            width: 100%;
        `,
    },
    linksContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    cancelPunchOutButton: linkIconStyleGenerator({
        icon: "Cancel",
        textSize: "13px",
        iconSize: 14,
        wrapperCss: css`
            margin-bottom: 20px;
        `,
    }),
    submitQuoteButton: linkIconStyleGenerator({
        icon: "Info",
        textSize: "13px",
        iconSize: 14,
        wrapperCss: css`
            margin-bottom: 20px;
        `,
    }),
    submitRequisitionButton: linkIconStyleGenerator({
        icon: "Quote",
        textSize: "13px",
        iconSize: 14,
        wrapperCss: css`
            margin-bottom: 20px;
        `,
    }),
};

const styles = cartTotalStyles;

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    // Xng-Change
    isAuth,
    checkoutReviewAndSubmitPageUrl,
    enablePostalCodeLocationFinder,
    signInUrl,
}: Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const messagesContext = React.useContext(CartPageContainerMessagesContext);
    const [hasNoPostalCode, setHasNoPostalCode] = React.useState(false);

    React.useEffect(() => {
        if (enablePostalCodeLocationFinder && !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) && !isAuth) {
            setHasNoPostalCode(true);
        }
    }, []);

    if (isCartEmpty) {
        return null;
    }
    const checkoutHandler = () => {
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                // XNG-Changes
                // signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(onePageCheckoutPageUrl)}`);
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            // to refresh the page and get the new cart lines properties
            window.location.href = rfqRequestQuotePageUrl;
        }
    };

    const submitForQuoteLabel = cart?.isSalesperson
        ? translate("Create a Quote")
        : translate("Request Quote for All Items");

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    return (
        <>
            <CartTotalDisplay
                showTaxAndShipping={showTaxAndShipping}
                extendedStyles={styles.cartTotal}
                isLoading={isLoading}
                isCartEmpty={isCartEmpty}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
                // XNG-Changes
                {...{ showTaxMessage: true }}
            />
            {fields.includeActions !== false && (
                <>
                    <Button
                        {...styles.checkoutButton}
                        disabled={isCartCheckoutDisabled}
                        onClick={checkoutHandler}
                        data-test-selector="cartTotal_checkout"
                    >
                        {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                    </Button>
                    <TwoButtonModal
                        headlineText=""
                        modalIsOpen={quoteRequiredModalIsOpen}
                        messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={() => setQuoteRequiredModalIsOpen(false)}
                        onSubmit={checkoutHandler}
                        submitTestSelector="QuoteInCartMessageSubmit"
                        dataTestSelector="twoButtonModal_RequiresQuote"
                    ></TwoButtonModal>
                </>
            )}

            <StyledWrapper {...styles.linksContainer}>
                {fields.includeActions !== false && canSubmitForQuote && (
                    <Link
                        {...styles.submitQuoteButton}
                        disabled={isSubmitForQuoteButtonDisabled}
                        onClick={submitForQuoteClickHandler}
                        data-test-selector="cartTotal_submitQuote"
                    >
                        {submitForQuoteLabel}
                    </Link>
                )}
                {fields.includeActions !== false && canSubmitRequisition && (
                    <Link
                        disabled={isSubmitRequisitionButtonDisabled}
                        {...styles.submitRequisitionButton}
                        onClick={submitRequisitionClickHandler}
                        data-test-selector="cartTotal_submitRequisition"
                    >
                        {translate("Submit Requisition")}
                    </Link>
                )}
                {fields.includeActions !== false && isPunchOutOrder && canCheckoutWithCart && (
                    <Link
                        {...styles.cancelPunchOutButton}
                        disabled={isPunchoutButtonDisabled}
                        onClick={cancelPunchOutHandler}
                    >
                        {translate("Cancel PunchOut")}
                    </Link>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
